import { Overlay, OverlayPositionBuilder, OverlayKeyboardDispatcher, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { findLast, remove } from 'lodash-es';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { AlertType, ConfirmModalComponent, IConfirmModalParameters } from '../components/confirm-modal/confirm-modal.component';
import { ModalConfiguration } from '../models/modal-configuration.model';
import { Modal } from '../models/modal.model';

export enum HasChangesPopupResult {
  ExitWithoutSave = 'ExitWithoutSave',

  ExitWithSave = 'ExitWithSave',

  Cancel = 'Cancel',
}

export interface IAlertDialogParameters {
  confirmText?: string;
  confirmIcon?: string;
  confirmIconWeight?: string;
}

export interface IConfirmDialogParameters extends IAlertDialogParameters {
  rejectText?: string;
  buttons?: Array<{ text: string; result: any; common?: boolean; isDisabled?: boolean }>;
}

export interface IPersistOnCloseAll {
  persistOnCloseAll: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService implements OnDestroy {
  private readonly stack: Array<Modal> = [];
  private readonly stackMaterial: Array<{ close: () => void }> = [];
  private activeListener: Modal;
  private readonly subscription$: Subscription = new Subscription();

  public constructor(
    private readonly overlay: Overlay,
    private readonly overlayKeyboardDispatcher: OverlayKeyboardDispatcher,
    private readonly injector: Injector,
    private readonly matDialog: MatDialog,
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly router: Router,
  ) {
    this.setupCloseOnNavigationEnd();
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public open<TComponent, TParameters = void>(
    componentType: ComponentType<TComponent>,
    configuration: ModalConfiguration,
    parent?: Modal,
    parameters?: TParameters,
  ): Modal<TParameters> {
    return this.createModal<TComponent, TParameters>(componentType, configuration, parent, parameters);
  }

  public closeAll(): any {
    this.matDialog.openDialogs
      .filter((dialogRef) => !(dialogRef.componentInstance as IPersistOnCloseAll)?.persistOnCloseAll)
      .forEach((dialogRef) => dialogRef.close());
    this.stack.forEach((modal) => modal.close(true));
    this.stackMaterial.forEach((modal) => {
      remove(this.stackMaterial, modal);
      modal.close();
    });
  }

  public close(modal: Modal) {
    if (modal.child) {
      modal.child.close();
    }
    if (this.activeListener && this.activeListener === modal) {
      const prevListener = findLast(this.stack, (m: Modal) => m.configuration.listenOnKeyboardEvents && m !== modal);
      if (prevListener) {
        this.configureKeyListener(prevListener);
      } else {
        this.overlayKeyboardDispatcher.remove(this.activeListener.overlayRef);
      }
      this.activeListener = null;
    }
    remove(this.stack, (m: Modal) => modal);
    modal.overlayRef.dispose();
  }

  public showAlertPopup(
    type: AlertType,
    title: string,
    message: string,
    options: IAlertDialogParameters = { confirmText: TranslationKeys.GeneralOk },
  ): Modal {
    options = { confirmText: TranslationKeys.GeneralOk, ...options };

    return this.open(
      ConfirmModalComponent,
      {
        closable: false,
        listenOnKeyboardEvents: false,
        submitable: false,
      },
      undefined,
      {
        alertType: type,
        title,
        message,
        canReject: false,
        confirmIcon: options.confirmText,
        confirmText: options.confirmText,
        confirmIconWeight: options.confirmIconWeight,
      } as IConfirmModalParameters,
    );
  }

  public showLoadingPopup(type: AlertType, title: string, message: string): Modal {
    return this.open(
      ConfirmModalComponent,
      {
        closable: false,
        listenOnKeyboardEvents: false,
        submitable: false,
      },
      undefined,
      {
        alertType: type,
        title,
        buttons: undefined,
        message,
        canReject: false,
        confirmText: TranslationKeys.GeneralCancel,
        hideFooter: true,
        loading: true,
      } as IConfirmModalParameters,
    );
  }

  public showConfirmationPopup(
    type: AlertType,
    title: string,
    message: string,
    options: IConfirmDialogParameters = { confirmText: TranslationKeys.GeneralConfirm, rejectText: TranslationKeys.GeneralCancel },
  ): Modal {
    options = { confirmText: TranslationKeys.GeneralConfirm, rejectText: TranslationKeys.GeneralCancel, ...options };

    return this.open(
      ConfirmModalComponent,
      {
        closable: true,
        listenOnKeyboardEvents: true,
        submitable: true,
      },
      undefined,
      {
        alertType: type,
        title,
        message,
        canReject: true,
        confirmIcon: options.confirmIcon,
        confirmText: options.confirmText,
        confirmIconWeight: options.confirmIconWeight,
        rejectText: options.rejectText,
        buttons: options.buttons,
      } as IConfirmModalParameters,
    );
  }

  public showPendingChangesPopup(
    isSaveButtonEnabled: boolean,
    type: AlertType = AlertType.Warning,
    title: TranslationKeys = TranslationKeys.SettingsUnsavedChanges,
    message: TranslationKeys = TranslationKeys.SettingsUnsavedChangesQuestion,
  ): Modal {
    return this.showConfirmationPopup(type, title, message, {
      buttons: [
        { text: TranslationKeys.GeneralDiscard, result: HasChangesPopupResult.ExitWithoutSave },
        { text: TranslationKeys.GeneralSave, result: HasChangesPopupResult.ExitWithSave, isDisabled: !isSaveButtonEnabled },
      ],
    });
  }

  private createModal<TComponent, TParameters>(
    componentType: ComponentType<TComponent>,
    configuration: ModalConfiguration,
    parent: Modal,
    parameters: TParameters,
  ): Modal<TParameters> {
    const overlayRef = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.overlayPositionBuilder.global().centerHorizontally().centerVertically(),
      maxHeight: '100vh',
      backdropClass: 'nimbus-modal-backdrop',
      hasBackdrop: true,
      disposeOnNavigation: true,
    });

    const modal = new Modal<TParameters>(overlayRef, this, configuration, parameters);

    const modalInjector = Injector.create({ providers: [{ provide: Modal, useValue: modal }], parent: this.injector });
    this.attachContainerPortal(overlayRef, componentType, modalInjector);

    if (parent) {
      parent.child = modal;
    }
    this.configureKeyListener(modal);
    this.stack.push(modal);

    return modal;
  }

  private setupCloseOnNavigationEnd(): void {
    this.subscription$.add(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.closeAll()));
  }

  private attachContainerPortal<T>(overlayRef: OverlayRef, container: ComponentType<T>, injector: Injector): T {
    const compRef = overlayRef.attach<T>(new ComponentPortal(container, null, injector));
    return compRef.instance;
  }

  private configureKeyListener(modal: Modal): void {
    if (modal.configuration.listenOnKeyboardEvents) {
      if (this.activeListener) {
        this.overlayKeyboardDispatcher.remove(this.activeListener.overlayRef);
      }
      this.overlayKeyboardDispatcher.add(modal.overlayRef);
      this.activeListener = modal;
    }
  }
}
